<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { useField } from '@voix/composables/useField'
import VoixFieldHeader from '@voix/voix-layer/components/VoixFieldHeader.vue'
import FieldDescription from '@voix/voix-layer/components/FieldDescription.vue'
import DropdownSelect from '@voix/components/chrome/controls/DropdownSelect.vue'

import VoixFileSelector from '@voix/voix-layer/components/VoixFileSelector.vue'
import SectionLabel from '@voix/components/chrome/SectionLabel.vue'
import TextInput from '@voix/components/chrome/controls/TextInput.vue'
import VoixDrop from '@voix/components/chrome/drag-and-drop/VoixDrop.vue'
import { v4 as uuidv4 } from 'uuid'

import { usePageStore } from '@voix/store/pageStore'

import { dragEnd, dragStart } from '@voix/composables/useDraggable'

import type { PropType, Ref } from 'vue'
import type {
  FieldConfigInterface,
  FieldInterface,
  SliceInterface,
  VoixFilesFieldValue,
} from '@voix/types'

const props = defineProps({
  element: {
    type: Object as PropType<SliceInterface>,
    required: false,
  },

  isPageField: {
    type: Boolean,
    required: false,
    default: false,
  },

  fieldData: {
    type: Object as PropType<FieldInterface>,
    default: null,
  },

  fieldConfiguration: {
    type: Object as PropType<FieldConfigInterface>,
    required: true,
  },
})

const { localValue, enabled } = useField(props)

const pageStore = usePageStore()

watch(
  () => localValue,
  (newValue) => {
    if (props.element) {
      pageStore.updateField(
        props.element.id,
        props.fieldConfiguration.name,
        newValue.value,
      )
    }
  },
  { deep: true },
)

const files = computed(() => {
  const array = localValue.value ? localValue.value : []
  // Add uuid to each file for sorting purposes
  array.forEach((file: VoixFilesFieldValue) => {
    if (!file.id)
      file.id = uuidv4()
  })
  return array
})

function onFileUpload(response: any) {
  if (response.data.value) {
    if (!localValue.value || !localValue.value)
      localValue.value = []

    // See if the file is already in the list
    const existingFile = localValue.value.find((
      file: { path: string, url: string },
    ) => file.url === response.data.value.url)

    if (existingFile)
      return

    localValue.value.push({
      url: response.data.value.url,
      type: response.file.type,
      provider: 'local',
    })
  }
}

function removeFile(file: { path: string, url: string }) {
  if (!localValue.value)
    return

  const index = localValue.value.findIndex((f: { path: string, url: string }) => f.url === file.url)

  if (index === -1)
    return

  localValue.value.splice(index, 1)
}

const gridMode = ref(false)

// When an element is dropped at the top of the data array
function dropTopOfDataArray(id: string) {
  // find the file from localValue.value with the id passed in
  const file = localValue.value.find((f: { id: string }) => f.id.toString() === id.toString())
  // Move that file to the top of the array
  if (file) {
    const index = localValue.value.indexOf(file)
    localValue.value.splice(index, 1)
    localValue.value.unshift(file)
  }
}

// When an element is dropped in the middle of the data array on any divider that
// isn't that top one. This could be in between a root level slice or inside a slice
// as a child.
function dropMiddleOfDataArray(id: string, destinationId: string) {
  const file = localValue.value.find((f: { id: string }) => f.id.toString() === id.toString())
  // Move that file to after the destination id
  if (file) {
    const index = localValue.value.indexOf(file)
    localValue.value.splice(index, 1)
    const destinationIndex = localValue.value.findIndex((f: { id: string }) => f.id.toString() === destinationId.toString())
    localValue.value.splice(destinationIndex + 1, 0, file)
  }
}

function fileName(url: string) {
  // Get the file name from the url
  const file = url.split('/').pop()
  return file
}

const sortFilesBy: Ref< string> = ref('')
watch(sortFilesBy, () => {
  // Sort the files based on the sortFilesBy value
  if (sortFilesBy.value === 'label-asc')
    localValue.value.sort((a: { title: string }, b: { title: string }) => a.title.localeCompare(b.title))
  else if (sortFilesBy.value === 'label-desc')
    localValue.value.sort((a: { title: string }, b: { title: string }) => b.title.localeCompare(a.title))
  else if (sortFilesBy.value === 'value-asc')
    localValue.value.sort((a: { url: string }, b: { url: string }) => a.url.localeCompare(b.url))
  else if (sortFilesBy.value === 'value-desc')
    localValue.value.sort((a: { url: string }, b: { url: string }) => b.url.localeCompare(a.url))
})
</script>

<template>
  <div>
    <VoixFieldHeader v-model="enabled" :field-configuration="props.fieldConfiguration" />

    <div class="flex flex-col space-y-4">
      <VoixFileSelector :multiple="fieldConfiguration.multiple" @file-upload="onFileUpload" />
    </div>

    <div>
      <DropdownSelect
        id="sort-files"
        v-model="sortFilesBy"
        label="Current Files - Sort Files By"
        :options="[
          {
            label: 'Label Ascending',
            value: 'label-asc',
            key: 'label-asc',
          },
          {
            label: 'Label Descending',
            value: 'label-desc',
            key: 'label-desc',
          },
          {
            label: 'Value Ascending',
            value: 'value-asc',
            key: 'value-asc',
          },
          {
            label: 'Value Descending',
            value: 'value-desc',
            key: 'value-desc',
          },
        ]"
        placeholder="Select a Loading Mode"
        class="mt-4"
      />
    </div>

    <VoixDrop
      v-slot="{ item }"
      :entire-data-array="files"
      :data-array="files"
      class="grid gap-3"
      :class="{
        'grid-cols-3': gridMode,
        'grid-cols-1': !gridMode,
      }"
      @drop-at-top="dropTopOfDataArray"
      @drop-in-middle="dropMiddleOfDataArray"
    >
      <div
        :id="item.id"
        :key="item.id"
        class="relative flex-none rounded flex items-center hover:bg-gray-200 py-2 cursor-grab"
        :class="{
          'w-20 h-24 ': gridMode,
          'w-full space-x-5': !gridMode,
        }"

        draggable="true"
        @dragstart="dragStart($event, `${item.id}`)"
        @dragend="dragEnd"
      >
        <div v-show="!gridMode" class="flex-1 text-sm break-words flex flex-col space-y-1">
          <div class=" flex items-center -ml-1 space-x-2">
            <TextInput
              :id="`file-${item.id}-title`"
              v-model="item.title"
              placeholder="Label (optional)"
              class="w-full"
            >
              <Icon name="heroicons:chat-bubble-left-20-solid" class="w-4 h-4" />
            </TextInput>
            <div
              class="flex items-center justify-center space-x-1 py-1 mt-1"
            >
              <button class="voix-admin-text p-2 rounded bg-white flex justify-center" @click="removeFile(item)">
                <Icon class="w-3.5 h-3.5" name="heroicons:trash" />
              </button>
              <a class="voix-admin-text p-2 rounded bg-white flex justify-center" :href="item.url" target="_blank">
                <Icon class="w-3.5 h-3.5" name="heroicons:link-20-solid" />
              </a>
            </div>
          </div>
          <input type="text" disabled :value="fileName(item.url)" class="text-xs bg-gray-50 rounded w-full border-gray-200 border px-3 py-2 ">
        </div>
      </div>
    </VoixDrop>

    <FieldDescription :field-configuration="props.fieldConfiguration" />
  </div>
</template>
